/// <reference types="vite/client" />

import { createRouter, createWebHistory } from 'vue-router'
import FormView from '../views/FormView.vue'
import TermsView from '../views/TermsView.vue'
import EntryView from '../views/EntryView.vue'
import DashboardView from '../views/DashboardView.vue'
import AnalyticsView from '../views/AnalyticsView.vue'
import ExperienceLibraryView from '../views/ExperienceLibraryView.vue'
import ExperienceEditorView from '../views/ExperienceEditorView.vue'
import BillingView from '../views/BillingView.vue'
import BillingSuccess from '../views/BillingSuccess.vue'
import ProfileView from '../views/ProfileView.vue'
import InviteAcceptView from '../views/InviteAcceptView.vue'
import HoldingPage from '@/components/HoldingPage.vue'
import { useAuthStore } from '@/store/auth.store'
import { config } from '@/config/config'

const isHoldingPageActive = config.isHoldingPageActive

const routes = [
  {
    path: '/',
    name: 'Home',
    component: isHoldingPageActive ? HoldingPage : FormView,
    meta: { showProgressBar: !isHoldingPageActive },
  },
  {
    path: '/terms',
    name: 'Terms',
    component: TermsView,
    alias: ['/terms-and-conditions'],
  },
  {
    path: '/entry',
    name: 'Entry',
    component: EntryView,
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Dashboard', helpKey: 'dashboard' },
  },
  {
    path: '/dashboard/campaigns/:campaignId/edit',
    name: 'DashboardCampaignEdit',
    component: FormView,
    meta: { requiresAuth: true, showProgressBar: true, layout: 'app', sectionTitle: 'Edit campaign', helpKey: 'edit_campaign' },
  },
  {
    path: '/dashboard/analytics',
    name: 'DashboardAnalytics',
    component: AnalyticsView,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Analytics', helpKey: 'analytics' },
  },
  {
    path: '/dashboard/experiences',
    name: 'DashboardExperiences',
    component: ExperienceLibraryView,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Experience Library', helpKey: 'experiences' },
  },
  {
    path: '/dashboard/experiences/:experienceKey',
    name: 'DashboardExperienceEditor',
    component: ExperienceEditorView,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Experience Editor', helpKey: 'experience_editor' },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfileView,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Account', helpKey: 'account' },
  },
  {
    path: '/invite/:id',
    name: 'InviteAccept',
    component: InviteAcceptView,
  },
  {
    path: '/billing',
    name: 'billing',
    component: BillingView,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Billing', helpKey: 'billing' },
  },
  {
    path: '/billing/success',
    name: 'billing-success',
    component: BillingSuccess,
    meta: { requiresAuth: true, layout: 'app', sectionTitle: 'Billing', helpKey: 'billing' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior () {
    return { top: 0 }
  },
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // 1. If the store says "Not Logged In", try to restore the session from Cognito/LocalStorage first.
  if (!authStore.isAuthenticated) {
    try {
      await authStore.checkAuth();
    } catch (e) {
      // If checkAuth fails, they really are logged out. Proceed.
    }
  }

  // 2. Now perform the check with the updated state
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    // User is still not authenticated after the check -> Redirect to Home
    next('/')
    return
  }

  // 3. Feature gate: if the route requires a feature the user doesn't have, send to billing
  const requiredFeature = to.meta.requiredFeature as string | undefined
  if (requiredFeature && !authStore.features.includes(requiredFeature)) {
    next('/billing')
    return
  }

  next()
})

export default router
